import { useCallback, useRef } from 'react';

import { useMenuItemDragAndDrop } from '@atlaskit/navigation-system/side-nav-items/drag-and-drop/use-menu-item-drag-and-drop';

import { getMoreMenuData, isL1ItemData } from '../../../common/utils/dnd-data';
import { useElementRegistry } from '../../../controllers/element-registry';
import { useOptions } from '../../../controllers/options-context';

export function useMoreMenuDragAndDrop(shouldShowMoreMenu: boolean) {
	const { l1DragAndDropEnabled } = useOptions();

	const { dropIndicator, dropTargetRef } = useMenuItemDragAndDrop({
		// If the More menu will not be shown (e.g. no hidden items), we shouldn't set up drag and drop
		...(l1DragAndDropEnabled &&
			shouldShowMoreMenu && {
				dropTarget: {
					getData: () => getMoreMenuData(),
					getOperations: () => ({
						combine: 'available',
					}),
					canDrop: ({ source }) => {
						return isL1ItemData(source.data) && source.data.isHideable;
					},
				},
			}),
	});

	const registry = useElementRegistry();

	const registrationRef = useRef<(() => void) | null>(null);
	const moreButtonRef = useCallback(
		(element: HTMLButtonElement | null) => {
			if (!element) {
				registrationRef.current?.();
				registrationRef.current = null;

				return;
			}

			// Unregister previous element if exists
			if (registrationRef.current) {
				registrationRef.current();
			}

			// Register the new element with the registry
			registrationRef.current = registry.registerMoreMenuItem(element);
		},
		[registrationRef, registry],
	);

	return {
		dropIndicator,
		dropTargetRef,
		moreButtonRef,
	};
}
